import type { SubmissionFiles, SubmissionPayload, SubmissionResult } from '../domain/types';

/**
 * The one call this app makes to its own backend.
 *
 * In dev, Vite proxies /api to the Express port, so this stays same-origin and
 * behaves exactly as it will in production behind a single domain.
 */
const API_BASE = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/$/, '') ?? '';

interface ServerError {
  ok: false;
  error?: { code?: string; fields?: Record<string, string> };
}

export async function submitSubmission(
  payload: SubmissionPayload,
  files: SubmissionFiles,
  signal?: AbortSignal,
): Promise<SubmissionResult> {
  const form = new FormData();
  form.append('payload', JSON.stringify(payload));

  if (files.certificate) {
    form.append('certificate', files.certificate.file, files.certificate.file.name);
  }
  for (const entry of files.additional) {
    form.append('additional', entry.file, entry.file.name);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}/api/submissions`, {
      method: 'POST',
      body: form,
      signal: signal ?? null,
    });
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') throw error;
    // Offline, DNS failure, connection refused — anything before an HTTP status.
    return { ok: false, code: 'network' };
  }

  if (response.ok) {
    const body = (await response.json()) as { ok: true; id: string };
    return { ok: true, id: body.id };
  }

  // 413 can come from a reverse proxy as HTML, before Express ever sees it.
  if (response.status === 413) return { ok: false, code: 'too_large' };
  if (response.status === 429) return { ok: false, code: 'rate_limit' };

  let body: ServerError | null = null;
  try {
    body = (await response.json()) as ServerError;
  } catch {
    body = null;
  }

  if (response.status === 400) {
    const result: SubmissionResult = { ok: false, code: 'validation' };
    if (body?.error?.fields) result.fields = body.error.fields;
    return result;
  }

  return { ok: false, code: 'server' };
}
